import type {
  PersonEfteruddannelse,
  PersonEfteruddannelseIndberetning,
} from "@/interfaces/schema";
import {
  EfteruddannelseIndberetningStatusId,
  EfteruddannelseIndberetningstypeId,
} from "@/interfaces/schema";
import { periodeIsActive } from "@/util/date";
import { formatLessons, roundLessons } from "@/util/number";

/**
 * Efteruddannelsestypenumre. Rækkefølgen er AIKAs, se DocumentHelper i aika-backend.
 */
export const EFTERUDDANNELSESTYPE = {
  Reduktion: 1,
  Kursus: 2,
  Dispensation: 3,
  Forfatter: 4,
  Advokatvagt: 5,
  Undervisning: 6,
} as const;

/**
 * Lofter for OVERFØRSEL, som de håndhæves af indberetningsportalen i
 * `EfteruddannelseIndberetningDraftHelper` (aika-indberetning-backend). AIKA håndhæver dem ikke, så
 * de vises som advarsler, ikke som spærringer.
 *
 * OBS: Disse tal er duplikeret over en repo-grænse. Ændres reglerne, skal begge steder rettes.
 * Brug IKKE `Efteruddannelsestype.maxLektioner`/`overfoerPrioritet`/`overfoerTilMax` til dette —
 * de ser ud som reglerne, men er død AMK-importdata, som ingen beregning læser.
 */
export const MAX_OVERFOERT_PER_TYPE: Readonly<Record<number, number>> = {
  [EFTERUDDANNELSESTYPE.Reduktion]: 0,
  [EFTERUDDANNELSESTYPE.Kursus]: 54,
  [EFTERUDDANNELSESTYPE.Dispensation]: 0,
  [EFTERUDDANNELSESTYPE.Forfatter]: 18,
  [EFTERUDDANNELSESTYPE.Advokatvagt]: 0,
  [EFTERUDDANNELSESTYPE.Undervisning]: 27,
};

export const MAX_OVERFOERT_I_ALT = 54;
export const PAAKRAEVEDE_LEKTIONER_PER_PERIODE = 54;

/** De typer, der efter reglerne må overføres. */
export const OVERFOERBARE_TYPER: readonly number[] = Object.keys(
  MAX_OVERFOERT_PER_TYPE,
)
  .map(Number)
  .filter((typeNr) => MAX_OVERFOERT_PER_TYPE[typeNr] > 0)
  .sort((a, b) => a - b);

export const erOverfoerbar = (typeNr: number): boolean =>
  (MAX_OVERFOERT_PER_TYPE[typeNr] ?? 0) > 0;

const sumLinjerPerType = (
  indberetninger: Pick<
    PersonEfteruddannelseIndberetning,
    "personEfteruddannelseIndberetningLinje"
  >[],
): Map<number, number> => {
  const perType = new Map<number, number>();
  indberetninger.forEach((indberetning) => {
    indberetning.personEfteruddannelseIndberetningLinje.forEach((linje) => {
      perType.set(
        linje.efteruddannelsestypenr,
        roundLessons(
          (perType.get(linje.efteruddannelsestypenr) ?? 0) +
            linje.indberettedeLektioner,
        ),
      );
    });
  });
  return perType;
};

/**
 * Lektioner overført IND i perioden, som endpointet kan redigere: KUN Særlig-indberetningen.
 *
 * Bemærk at periodens viste "overført hertil" er Særlig + Påbud + Pålæg (se
 * getTransferredIndberetninger), men påbuds- og pålægsoverførsler skrives ikke af dette endpoint.
 * Derfor må formularen ikke seedes fra dem.
 */
export const getSaerligLektionerByType = (
  period: PersonEfteruddannelse,
): Map<number, number> =>
  sumLinjerPerType(
    period.personEfteruddannelseIndberetning.filter(
      (indberetning) =>
        indberetning.efteruddannelseIndberetningstype ===
        EfteruddannelseIndberetningstypeId.Saerlig,
    ),
  );

/** Overførsler fra påbud/pålæg. Vises read-only, kan ikke redigeres her. */
export const getInjunctionOverflowByType = (
  period: PersonEfteruddannelse,
): Map<number, number> =>
  sumLinjerPerType(
    period.personEfteruddannelseIndberetning.filter(
      (indberetning) =>
        indberetning.efteruddannelseIndberetningstype ===
          EfteruddannelseIndberetningstypeId.Paabud ||
        indberetning.efteruddannelseIndberetningstype ===
          EfteruddannelseIndberetningstypeId.Paalaeg,
    ),
  );

/**
 * Hvilke typer formularen skal vise: de overførbare, plus enhver anden type der allerede har en
 * værdi.
 *
 * Den sidste del er ikke kosmetik. Endpointet er fuld erstatning, så en type der udelades bliver
 * SLETTET — skjuler vi en gammel dispensationsoverførsel, forsvinder den ved næste gem.
 */
export const getVisibleTransferTypes = (
  currentValuesByType: Map<number, number>,
): number[] => {
  const synlige = new Set<number>(OVERFOERBARE_TYPER);
  currentValuesByType.forEach((lektioner, typeNr) => {
    if (lektioner !== 0) synlige.add(typeNr);
  });
  return [...synlige].sort((a, b) => a - b);
};

export interface OverfoerteLektionerFormValues {
  /** typeNr -> lektioner som indtastet. "" betyder tom. */
  lektionerPerType: Record<number, string>;
}

/**
 * Seeder formularen med periodens NUVÆRENDE overførte lektioner. Feltet er altså det samlede antal
 * efter rettelsen — ikke et beløb der lægges til.
 */
export const buildOverfoerteLektionerFormValues = (
  period: PersonEfteruddannelse,
): OverfoerteLektionerFormValues => {
  const nuvaerende = getSaerligLektionerByType(period);
  const lektionerPerType: Record<number, string> = {};
  getVisibleTransferTypes(nuvaerende).forEach((typeNr) => {
    const vaerdi = nuvaerende.get(typeNr) ?? 0;
    lektionerPerType[typeNr] = String(vaerdi);
  });
  return { lektionerPerType };
};

const parseLektioner = (raw: string | number | undefined): number => {
  if (raw === undefined || raw === "") return 0;
  const parsed = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Til request-body. Alle synlige typer sendes med — også dem der er 0, så backenden fjerner linjen.
 */
export const formValuesToOverfoerteLinjer = (
  values: OverfoerteLektionerFormValues,
): { efteruddannelsestypenr: number; indberettedeLektioner: number }[] =>
  Object.keys(values.lektionerPerType)
    .map(Number)
    .sort((a, b) => a - b)
    .map((typeNr) => ({
      efteruddannelsestypenr: typeNr,
      indberettedeLektioner: roundLessons(
        parseLektioner(values.lektionerPerType[typeNr]),
      ),
    }));

export interface OverfoerteLektionerDeltaRow {
  typeNr: number;
  foer: number;
  efter: number;
  aendring: number;
}

export interface OverfoerteLektionerDelta {
  rows: OverfoerteLektionerDeltaRow[];
  totalFoer: number;
  totalEfter: number;
  totalAendring: number;
}

/**
 * Før/efter pr. type. Bruges til at vise "10 → 18 (+8)", så en sekretær ikke taster 8 i et felt der
 * allerede står på 10 og derved sætter overførslen NED.
 */
export const getOverfoerteLektionerDelta = (
  initialValues: OverfoerteLektionerFormValues,
  values: OverfoerteLektionerFormValues,
): OverfoerteLektionerDelta => {
  const typeNumre = [
    ...new Set([
      ...Object.keys(initialValues.lektionerPerType).map(Number),
      ...Object.keys(values.lektionerPerType).map(Number),
    ]),
  ].sort((a, b) => a - b);

  const rows = typeNumre.map((typeNr) => {
    const foer = roundLessons(
      parseLektioner(initialValues.lektionerPerType[typeNr]),
    );
    const efter = roundLessons(parseLektioner(values.lektionerPerType[typeNr]));
    return { typeNr, foer, efter, aendring: roundLessons(efter - foer) };
  });

  const totalFoer = roundLessons(rows.reduce((sum, r) => sum + r.foer, 0));
  const totalEfter = roundLessons(rows.reduce((sum, r) => sum + r.efter, 0));

  return {
    rows,
    totalFoer,
    totalEfter,
    totalAendring: roundLessons(totalEfter - totalFoer),
  };
};

/** Summen af det, der er tastet. */
export const getOverfoertIAlt = (
  values: OverfoerteLektionerFormValues,
): number =>
  roundLessons(
    formValuesToOverfoerteLinjer(values).reduce(
      (sum, l) => sum + l.indberettedeLektioner,
      0,
    ),
  );

/**
 * Det ene loft, der er en spærring og ikke en advarsel: der kan højst overføres 54 lektioner i alt.
 * Backenden håndhæver samme regel, så et loft der kun sad her kunne omgås af et direkte kald.
 *
 * Returnerer undefined når totalen er lovlig.
 */
export const getOverfoertIAltError = (
  values: OverfoerteLektionerFormValues,
): string | undefined => {
  const total = getOverfoertIAlt(values);
  if (total <= MAX_OVERFOERT_I_ALT) return undefined;
  return `Der kan højst overføres ${MAX_OVERFOERT_I_ALT} lektioner i alt. Der er angivet ${total}.`;
};

export interface TransferCapWarning {
  typeNr?: number;
  message: string;
}

/**
 * Advarsler, ikke spærringer. Matcher husets egen linje for manuelt indtastede tal: serveren er med
 * vilje mere tolerant end reglen, så historiske og korrigerede tal kan gemmes.
 */
export const getTransferCapWarnings = (
  values: OverfoerteLektionerFormValues,
  typeLabels: Map<number, string>,
  lektionerIndberettetForrigePeriode?: number | null,
): TransferCapWarning[] => {
  const warnings: TransferCapWarning[] = [];
  const linjer = formValuesToOverfoerteLinjer(values);

  linjer.forEach(
    ({ efteruddannelsestypenr: typeNr, indberettedeLektioner }) => {
      if (indberettedeLektioner <= 0) return;
      const loft = MAX_OVERFOERT_PER_TYPE[typeNr];
      const navn = typeLabels.get(typeNr) ?? String(typeNr);
      if (loft === 0) {
        warnings.push({
          typeNr,
          message: `${navn} kan efter reglerne ikke overføres. Du kan gemme alligevel.`,
        });
      } else if (loft !== undefined && indberettedeLektioner > loft) {
        warnings.push({
          typeNr,
          message: `${navn} kan efter reglerne højst overføres med ${loft} lektioner. Du kan gemme alligevel.`,
        });
      }
    },
  );

  const total = getOverfoertIAlt(values);

  if (
    total > 0 &&
    lektionerIndberettetForrigePeriode != null &&
    lektionerIndberettetForrigePeriode < PAAKRAEVEDE_LEKTIONER_PER_PERIODE
  ) {
    warnings.push({
      message: `Lektioner kan efter reglerne først overføres, når der er indberettet ${PAAKRAEVEDE_LEKTIONER_PER_PERIODE} lektioner i den forrige periode (der er indberettet ${formatLessons(lektionerIndberettetForrigePeriode)}).`,
    });
  }

  return warnings;
};

/**
 * Må der registreres overførte lektioner PÅ denne periode?
 *
 * Kun perioder advokaten stadig kan handle på: den indeværende, eller en der er udløbet men endnu
 * ikke indberettet. Alt andet er lukket historik, hvor en registrering ikke gør nogen forskel —
 * den foregående periode har typisk ingen primær indberetning, så der er intet at synkronisere til
 * portalen, og advokaten ser den aldrig.
 *
 * Backenden håndhæver den samme regel; her holder vi bare knappen væk, så en sekretær ikke får
 * tilbudt en handling, der ville blive afvist.
 */
export const kanRegistrereOverfoerteLektioner = (
  period: PersonEfteruddannelse,
): boolean => {
  const erIndevaerende = periodeIsActive(
    new Date(period.periode.fraDato),
    new Date(period.periode.tilDatoInklusiv),
  );
  if (erIndevaerende) return true;

  return period.personEfteruddannelseIndberetning.some(
    (indberetning) =>
      indberetning.efteruddannelseIndberetningstype ===
        EfteruddannelseIndberetningstypeId.Periodeudloeb &&
      indberetning.efteruddannelseIndberetningStatus ===
        EfteruddannelseIndberetningStatusId.FristOrdinaer,
  );
};
